import { useEffect, useState } from "react";
import {
  CheckCircle2, Facebook, Globe, Linkedin, Loader2, Mail, MapPin,
  Phone, Save, Settings, Twitter, Youtube,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useContactInfo, useUpdateContactInfo, type ContactInfoRow,
} from "@/hooks/useContactInfo";
import { cn } from "@/lib/utils";

/**
 * /contact-info — Admin-only singleton editor for ContactInfo.
 * Powers the public /contact/ page + the footer's contact column.
 *
 * Backed by /api/v1/contact-info/singleton/ which is locked to Admin
 * role. The hook materialises the row on first GET so this form
 * always has something to bind to (no create-vs-edit branching).
 */
export default function ContactInfoPage() {
  const list   = useContactInfo();
  const update = useUpdateContactInfo();
  const [form, setForm] = useState<Partial<ContactInfoRow>>({});
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Seed once data arrives. Re-seed if the server payload changes
  // out-of-band (e.g. another admin updated the row).
  useEffect(() => {
    if (list.data) setForm(list.data);
  }, [list.data]);

  function setField<K extends keyof ContactInfoRow>(key: K, value: ContactInfoRow[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    // Strip id / updated_at — they're server-managed.
    const { id: _id, updated_at: _u, ...patch } = form;
    update.mutate(patch, {
      onSuccess: () => setSavedAt(new Date()),
    });
  }

  const busy = update.isPending;

  if (list.isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4">
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* ---------- Header ---------- */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            <Settings className="h-3.5 w-3.5" />Public contact details
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Contact info</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Powers the public{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">/contact/</code>{" "}
            page and the contact column in the site footer. Leave any field
            blank to hide it from the public site — only populated fields
            render.
          </p>
        </div>
        {savedAt && !busy && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Saved {savedAt.toLocaleTimeString()}
          </span>
        )}
      </header>

      <form onSubmit={handleSave} className="space-y-5">

        {/* ---------- Header / intro ---------- */}
        <Section title="Header" description="Top-of-page branding and intro paragraph.">
          <Field label="Office / organisation name">
            <Input
              value={form.office_name ?? ""}
              onChange={(e) => setField("office_name", e.target.value)}
              placeholder="Ministry of Planning and Development"
              maxLength={200}
              disabled={busy}
            />
          </Field>
          <Field label="Intro paragraph" hint="Shown under the hero on the /contact/ page.">
            <Textarea
              value={form.intro_text ?? ""}
              onChange={(e) => setField("intro_text", e.target.value)}
              rows={3}
              placeholder="Brief paragraph welcoming visitors and explaining what kinds of inquiries you handle."
              disabled={busy}
            />
          </Field>
        </Section>

        {/* ---------- Address ---------- */}
        <Section title="Address" icon={MapPin}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Address line 1">
              <Input
                value={form.address_line_1 ?? ""}
                onChange={(e) => setField("address_line_1", e.target.value)}
                maxLength={300}
                disabled={busy}
              />
            </Field>
            <Field label="Address line 2">
              <Input
                value={form.address_line_2 ?? ""}
                onChange={(e) => setField("address_line_2", e.target.value)}
                maxLength={300}
                disabled={busy}
              />
            </Field>
            <Field label="City">
              <Input
                value={form.city ?? ""}
                onChange={(e) => setField("city", e.target.value)}
                maxLength={100}
                disabled={busy}
              />
            </Field>
            <Field label="Country">
              <Input
                value={form.country ?? ""}
                onChange={(e) => setField("country", e.target.value)}
                maxLength={100}
                disabled={busy}
              />
            </Field>
          </div>
        </Section>

        {/* ---------- Contact channels ---------- */}
        <Section title="Contact channels" icon={Phone}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Primary phone">
              <Input
                value={form.phone_primary ?? ""}
                onChange={(e) => setField("phone_primary", e.target.value)}
                placeholder="+251 11 140 3049"
                maxLength={50}
                disabled={busy}
              />
            </Field>
            <Field label="Secondary phone (optional)">
              <Input
                value={form.phone_secondary ?? ""}
                onChange={(e) => setField("phone_secondary", e.target.value)}
                maxLength={50}
                disabled={busy}
              />
            </Field>
            <Field label="Email" icon={Mail}>
              <Input
                type="email"
                value={form.email ?? ""}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="info@mopd.gov.et"
                maxLength={254}
                disabled={busy}
              />
            </Field>
            <Field label="Website" icon={Globe}>
              <Input
                type="url"
                value={form.website ?? ""}
                onChange={(e) => setField("website", e.target.value)}
                placeholder="https://mopd.gov.et"
                disabled={busy}
              />
            </Field>
            <Field label="Office hours" hint='e.g. "Mon – Fri, 8:30 AM – 5:30 PM (EAT)"'>
              <Input
                value={form.office_hours ?? ""}
                onChange={(e) => setField("office_hours", e.target.value)}
                maxLength={200}
                disabled={busy}
              />
            </Field>
          </div>
        </Section>

        {/* ---------- Map embed ---------- */}
        <Section
          title="Map embed"
          description="Paste only the `src` URL from a Google Maps embed iframe. Leave blank to use the default MoPD HQ location."
        >
          <Field label="Map embed URL">
            <Input
              type="url"
              value={form.map_embed_url ?? ""}
              onChange={(e) => setField("map_embed_url", e.target.value)}
              placeholder="https://www.google.com/maps/embed?pb=..."
              disabled={busy}
            />
          </Field>
        </Section>

        {/* ---------- Social ---------- */}
        <Section title="Social links" description="Each URL renders as a pill in the contact-channels panel.">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Facebook" icon={Facebook}>
              <Input
                type="url"
                value={form.facebook_url ?? ""}
                onChange={(e) => setField("facebook_url", e.target.value)}
                placeholder="https://facebook.com/..."
                disabled={busy}
              />
            </Field>
            <Field label="Twitter / X" icon={Twitter}>
              <Input
                type="url"
                value={form.twitter_url ?? ""}
                onChange={(e) => setField("twitter_url", e.target.value)}
                placeholder="https://twitter.com/..."
                disabled={busy}
              />
            </Field>
            <Field label="LinkedIn" icon={Linkedin}>
              <Input
                type="url"
                value={form.linkedin_url ?? ""}
                onChange={(e) => setField("linkedin_url", e.target.value)}
                placeholder="https://linkedin.com/company/..."
                disabled={busy}
              />
            </Field>
            <Field label="YouTube" icon={Youtube}>
              <Input
                type="url"
                value={form.youtube_url ?? ""}
                onChange={(e) => setField("youtube_url", e.target.value)}
                placeholder="https://youtube.com/@..."
                disabled={busy}
              />
            </Field>
          </div>
        </Section>

        {/* ---------- Save bar ---------- */}
        <div className="sticky bottom-4 z-10 flex justify-end">
          <Button type="submit" size="lg" disabled={busy} className="shadow-lg">
            {busy
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
              : <><Save     className="mr-2 h-4 w-4" />Save changes</>}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section + Field helpers — keep the long form readable.
// ---------------------------------------------------------------------------
function Section({
  title, description, icon: Icon, children,
}: {
  title: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <Card className="border-border/60 shadow-soft">
      <CardHeader className="border-b border-border/60 bg-muted/30 py-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.08em] text-foreground">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          {title}
        </CardTitle>
        {description && (
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4 p-5">{children}</CardContent>
    </Card>
  );
}

function Field({
  label, hint, icon: Icon, children,
}: {
  label: string;
  hint?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className={cn("flex items-center gap-1.5", Icon && "text-muted-foreground")}>
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
